import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { Component } from '@angular/core';
import {
    UDFCellRendererParams,
    UDFChoiceCellRendererParams,
    UDFDateCellRendererParams,
} from '@ddv/data-grid';
import { UserDefinedField, UserDefinedFieldType } from '@ddv/models';
import { isUDFChoiceCellRendererParams, isUDFDateCellRendererParams } from '@ddv/utils';
import { DropdownOption } from '@hs/ui-core-common';
import { CommonDate, FuzzyDate } from '@hs/ui-core-date';

@Component({
    selector: 'app-user-defined-field-cell-renderer',
    templateUrl: './user-defined-field-cell-renderer.component.html',
    styleUrls: ['./user-defined-field-cell-renderer.component.scss'],
    standalone: false,
})
export class UserDefinedFieldCellRendererComponent implements ICellRendererAngularComp {
    cellValue: string | number | undefined;
    dropdownOptions: DropdownOption[] = [];
    dropdownCellValue: DropdownOption | undefined;
    dateCellValue: CommonDate | undefined;
    booleanCellValue = false;

    protected params: UDFCellRendererParams | UDFChoiceCellRendererParams | UDFDateCellRendererParams | undefined;
    protected type: UserDefinedFieldType | undefined;
    protected fuzzyDates: FuzzyDate[] = [];
    protected canEditUdf = false;

    agInit(params: UDFCellRendererParams | UDFChoiceCellRendererParams | UDFDateCellRendererParams): void {
        this.params = params;
        this.initializeCell(params);
        this.setCellValue();
    }

    refresh(params: UDFCellRendererParams | UDFChoiceCellRendererParams | UDFDateCellRendererParams): boolean {
        this.params = params;
        this.initializeCell(params);
        this.setCellValue();
        return true;
    }

    updateDropdownValue(e: DropdownOption): void {
        const field = this.params?.colDef?.field;
        if (!field) {
            return;
        }

        const newData = { ...this.params?.data[field] };
        newData.value = e?.value || null;
        const colId = this.params?.colDef?.colId;
        if (colId) {
            this.params?.node.setDataValue(colId, newData);
        }
    }

    updateDateValue(e: CommonDate | FuzzyDate | undefined): void {
        const field = this.params?.colDef?.field;
        if (!field) {
            return;
        }

        const newData = { ...this.params?.data[field] };
        if (e instanceof FuzzyDate) {
            newData.value = CommonDate.fromISOFormat(e.value).toDashFormat();
        } else if (e instanceof CommonDate) {
            newData.value = CommonDate.fromYearMonthDay(e.year, e.month, e.day).toDashFormat();
        }

        const colId = this.params?.colDef?.colId;
        if (colId) {
            this.params?.node.setDataValue(colId, newData);
        }
    }

    updateBooleanValue(e: boolean): void {
        const field = this.params?.colDef?.field;
        if (!field) {
            return;
        }

        const newData = { ...this.params?.data[field] };
        newData.value = e;
        const colId = this.params?.colDef?.colId;
        if (colId) {
            this.params?.node.setDataValue(colId, newData);
        }
    }

    private initializeCell(params: UDFCellRendererParams | UDFChoiceCellRendererParams | UDFDateCellRendererParams): void {
        this.canEditUdf = !!params?.canEditUdf;
        this.type = params?.userDefinedFieldType;
        if (isUDFChoiceCellRendererParams(params)) {
            this.dropdownOptions = params.choices?.map((c: string) => ({ text: c, value: c })) ?? [];
        } else if (isUDFDateCellRendererParams(params)) {
            this.fuzzyDates = params.fuzzyDates || [];
        }
    }

    private setCellValue(): void {
        const udfType = this.params?.userDefinedFieldType;
        if (udfType === 'decimal') {
            if (this.params?.node.isRowPinned()) {
                const colId = this.params?.colDef?.colId;
                if (colId) {
                    this.cellValue = this.params?.node.aggData[colId];
                }
                return;
            }

            if (this.params?.api.isPivotMode() || !!this.params?.node.allChildrenCount) {
                this.cellValue = this.params.value;
                return;
            }
        }

        const field = this.params?.colDef?.field;
        if (field) {
            const cellData: UserDefinedField = this.params?.data?.[field];
            if (cellData) {
                this.assignCellValueByType(udfType, cellData.value);
            }
        }
    }

    private assignCellValueByType(udfType?: UserDefinedFieldType, value?: string | number | boolean | null): void {
        switch (udfType) {
            case 'choice':
                this.dropdownCellValue = value ? { text: value as string, value: value as string } : undefined;
                break;
            case 'date':
                this.dateCellValue = value ? CommonDate.fromDashFormat(value as string) : undefined;
                break;
            case 'decimal':
                this.cellValue = this.params?.valueFormatted ?? this.params?.value?.value ?? value ?? 0;
                break;
            case 'string':
                this.cellValue = String(value != null ? value : '');
                break;
            case 'boolean':
                this.booleanCellValue = !!value;
                break;
        }
    }
}
